import React, { Component } from 'react';
import qs from 'query-string';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import axios from 'axios';
import compose from 'lodash/fp/compose';
import isEmpty from 'lodash/isEmpty';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import { Typography } from '@material-ui/core';
import { TextField, Button } from '@lavu-pos/lavu-react-components';
import { Link, withRouter } from 'react-router-dom';
import { string as yupString, object as yupObject } from 'yup';
import { Formik } from 'formik';

import { SECTIONS_OBJ } from 'common/constants';
import SwitchCP4Modal from 'common/SwitchCP4Modal';
import ContactInfoModal from 'common/ContactInfoModal';

import {
	lStorage,
	cookiesHelper,
	StyledLink,
	Colors,
	Spacer,
	intlShape,
	notifyToast,
} from 'modules/utils';

import { LOGIN_2FA_URL, LOGIN_URL, PHP_URL, REGENERATE_OTP_URL } from 'utils/constants';

import { withAuthContext } from './context';
import { LEVEL_3_USER, RESTRICTED_USER, INTERNAL_ERROR_MSG } from './constants';
import { LinkWrapper, StyledForm, StyledFormMessage } from './styles';
import { hasDismissedSwitchModal } from './helpers';
import {apolloSubscriptionTransportClient} from "../ApolloSubscriptionTransportClient";

import { loadProtect } from './Protect';

const validationSchema = yupObject().shape({
	username: yupString().required(
		<FormattedMessage id="loginErrors.empty.username" />
	),
	password: yupString().required(
		<FormattedMessage id="loginErrors.empty.password" />
	),
	twoFACode: yupString().when('show2FA', {
		is: true,
		then: yupString().required(
			<FormattedMessage id="loginErrors.empty.twoFACode" />
		),
		otherwise: yupString(),
	}),
});

class LoginForm extends Component {
	static propTypes = {
		updateUsername: PropTypes.func.isRequired,
		username: PropTypes.string.isRequired,
		history: PropTypes.shape({
			push: PropTypes.func,
		}),
		loginUrl: PropTypes.string,
		login2FAUrl: PropTypes.string,
		generateOTPUrl: PropTypes.string,
		title: PropTypes.string,
		width: PropTypes.string,
		isAdmin: PropTypes.bool,
		intl: intlShape,
	};

	static defaultProps = {
		loginUrl: LOGIN_URL,
		login2FAUrl: LOGIN_2FA_URL,
		generateOTPUrl: REGENERATE_OTP_URL,
		title: 'loginTitle',
		isAdmin: false,
	};

	state = {
		errorMessage: null,
		errorType: null, // Track the specific error type for better logic handling
		disabledSubmit: true,
		loginSuccess: false,
		accessLevel: false,
		// showSwitchCP4Modal: false, removing pop up for new users
		showContactInfoModal: false,
		isNewCPBusinessContactPopupCancelled: false,
		user: null,
		username: '',
		show2FA: false,
		twoFACode: '',
		twoFATimer: null,
		twoFAResendDisabled: true,
		twoFATimeRemaining: 300,
		twoFAGeneratedAt: null,
		loginData: null,
		formPassword: '',
		businessInfo: {
			address: '',
			city: '',
			country: '',
			emailid: '',
			phone: '',
			primary_contact: '',
			state: '',
			zip: '',
		},
	};

	onChangeForm = username => {
		const { updateUsername } = this.props;
		updateUsername(username);
	};

	filterQueryParams = () => {
		// Exclude locationid from query params coming from old CP
		const { locationid, ...params } = qs.parse(
			window.location.search || 'mode=home_home'
		);

		if (this.state.isNewCPBusinessContactPopupCancelled ) {
			params['new_cp_business_contact_popup_cancelled'] = 1;
		}

		return qs.stringify(params);
	};

	navigateToOldCP = () => {
		window.location.href = `${PHP_URL}?${this.filterQueryParams()}`;
	};

	onContactInfoCompleted = () => {
		const { user, username } = this.state;

		this.setState({
			showContactInfoModal: false,
		}, () => this.doLogin(user, username));
	};

	onContactInfoCancelled = () => {
		const { user, username } = this.state;

		this.setState({
			showContactInfoModal: false,
			isNewCPBusinessContactPopupCancelled: true,
		}, () => this.doLogin(user, username));
	};

	onContactInfoError = () => {
		const {
			intl: { formatMessage },
		} = this.props;

		notifyToast({
			kind: 'error',
			message: formatMessage({ id: 'hubqlErrors.UnknownError' }),
		});
	};

	doLogin = (user, username) => {
		const { history } = this.props;

		if (!user.isNewControlPanelActive) {
			this.navigateToOldCP();    //removing see later pop up 

			//below commented code is for enabling the pop up which will ask to start at old cp

			// if (user.isCP3Beta) {
			// 	const lastDismissedByUser =
			// 		lStorage.get({
			// 			key: 'SWITCH_CP4_MODAL_LAST_DISMISSED',
			// 			stringify: true,
			// 		}) || {};

			// 	if (!hasDismissedSwitchModal(lastDismissedByUser, username)) {
			// 		this.setState({
			// 			showSwitchCP4Modal: true,
			// 		});
			// 	} else {
			// 		this.navigateToOldCP();
			// 	}
		} else {
			console.log("6 here",user.isNewControlPanelActive);
			const { accessLevel, accessWhiteList, restrictedUser } = user;
			let redirectUrl = '/home';

			if (
				accessLevel === LEVEL_3_USER &&
				restrictedUser === RESTRICTED_USER
			) {
				const accessWhiteListSet = new Set(accessWhiteList.split(','));

				// Depend of the modules available for the user we're going to redirect
				// to the first one in the sidebar
				Object.keys(SECTIONS_OBJ).some(key => {
					if (accessWhiteListSet.has(SECTIONS_OBJ[key].name)) {
						redirectUrl = `/${SECTIONS_OBJ[key].url}`;
						return true;
					}

					return false;
				});
			}

			this.setState(
				{
					errorMessage: null,
					errorType: null,
					disabledSubmit: true,
				},
				() => {
					history.push(redirectUrl);
					window.scrollTo(0, 0);
				}
			);
		 }
	};

	is2FAExpired = () => {
		if (!this.state.twoFAGeneratedAt) {
			return false;
		}
		const now = Date.now();
		const elapsed = (now - this.state.twoFAGeneratedAt) / 1000; // in seconds
		return elapsed > 300; // 5 minute = 300 seconds
	};

	start2FATimer = () => {
		const now = Date.now();

		// Clear any existing timer to prevent multiple timers
		if (this.state.twoFATimer) {
			clearTimeout(this.state.twoFATimer);
		}

		this.setState({
			twoFAResendDisabled: true,
			twoFATimeRemaining: 300,
			twoFAGeneratedAt: now,
			twoFATimer: null,
		});

		const updateTimer = () => {
			this.setState(prevState => {
				// Check if component is still mounted and timer should continue
				if (
					!prevState.twoFAResendDisabled &&
					prevState.twoFATimeRemaining <= 0
				) {
					return { twoFATimer: null };
				}

				const newTime = prevState.twoFATimeRemaining - 1;
				if (newTime <= 0) {
					return {
						twoFAResendDisabled: false,
						twoFATimeRemaining: 0,
						twoFATimer: null,
					};
				}

				// Enable resend button after 30 seconds (when 210 seconds remain)
				const resendDisabled = newTime > 210;

				return {
					twoFAResendDisabled: resendDisabled,
					twoFATimeRemaining: newTime,
					twoFATimer: setTimeout(updateTimer, 1000),
				};
			});
		};

		// Start the timer immediately with the first update
		const timer = setTimeout(updateTimer, 1000);
		this.setState({ twoFATimer: timer });
	};

	handleResend2FA = async () => {
		// Prevent multiple simultaneous resend attempts
		if (this.state.twoFAResendDisabled) {
			return;
		}

		const { intl } = this.props;

		try {
			// Clear any existing error before resending
			this.setState({ errorMessage: null, errorType: null });

			// Call generateOTP API to resend the code
			await this.generateOTP();

			// If we reach here, generateOTP was successful and timer should be running
			// The timer is started inside generateOTP method
		} catch (error) {
			// Error handling is already done in generateOTP method
			// Stop the timer on failed resend attempts
			this.cleanup2FATimer();

			// Double-check that an error message was set. If not, set a fallback.
			// Use a small delay to ensure state updates from generateOTP have completed
			setTimeout(() => {
				if (!this.state.errorMessage) {
					this.setState({
						errorMessage: intl.formatMessage({
							id: 'loginErrors.otpSendFailed',
						}),
						errorType: 'otpSendFailed',
					});
				}
			}, 100);
		}
	};

	handleLoginSuccess = async (data, username) => {
		// If PHPSESSID throw Internal Error
		if (data.PHPSESSID && data.PHPSESSID !== 'null') {
			const {
				user: {
					languagePack: { googleCode },
				},
				token,
				PHPSESSID,
			} = data;

			// set the lavu-token when the login was successful
			cookiesHelper.set('PHPSESSID', PHPSESSID, 30, '/');
			cookiesHelper.set('LAVU_TOKEN', token, 30, '/');
			cookiesHelper.set('CP4_LOCALE', googleCode, 30, '/');
			// Set the cookie for the xsrf token
			// console.log("xsrf:" + data.user.xsrf_token);
			cookiesHelper.set('XSRF-TOKEN', data.user.xsrf_token, 30, '/');
			cookiesHelper.set('X-Lavu-Trace-Dn', data.user.dataname, 30, '/');
			const userType = data.user.lavuAdmin === 1 ? 'ainf' : 'uinf';
			const userRemoveType = data.user.lavuAdmin === 1 ? 'uinf' : 'ainf';
			cookiesHelper.remove(`${userRemoveType}`);
			cookiesHelper.set(
				`${userType}`,
				data.user.lavu_main_customer_accounts_id,
				30,
				'/'
			);
			lStorage.set({ key: 'current-user', value: { user: data.user } });

			// set the enviroment variables after login
			// eslint-disable-next-line no-underscore-dangle
			if (!window?._env_) {
				// eslint-disable-next-line no-underscore-dangle
				window._env_ = {};
			}
			Object.keys(data.cp3Env).forEach(key => {
				// eslint-disable-next-line no-underscore-dangle
				window._env_[key] = data.cp3Env[key];
			});
		} else {
			const err = new Error(INTERNAL_ERROR_MSG);
			err.response = {
				data: {
					code: 'InternalError',
				},
			};

			throw err;
		}

		const { user } = data;
		const { businessInfo, lavuAdmin } = user;
		const { contact_info_required: contactInfoRequired } = businessInfo;
		if (contactInfoRequired && !lavuAdmin) {
			this.setState({
				businessInfo,
				username,
				user,
				showContactInfoModal: true,
			});
		} else {
			this.doLogin(user, username);
		}
	};

	generateOTP = async () => {
		const { generateOTPUrl, intl } = this.props;
		const { user } = this.state;

		try {
			const { data } = await axios.post(
				generateOTPUrl,
				{
					username: user.username,
					mobileNumber: user.mobileNumber,
					userId: user.id,
					dataname: this.state.loginData.user.dataname,
					source: 'cp3',
				},
				{ withCredentials: true }
			);

			// Check if the response indicates success
			// Success can be indicated by:
			// 1. data.success === true
			// 2. data.status === 'success'
			// 3. HTTP 200 with data.code === 200 (successful OTP send)
			// 4. data.status === 'queued' (Twilio queued status is success)
			if (
				data.success ||
				data.status === 'success' ||
				data.code === 200 ||
				data.status === 'queued'
			) {
				this.start2FATimer();
				return data;
			}
			// Use backend error code if available, otherwise use message
			const errorMessage = this.getOTPErrorMessage(data, intl);
			this.setState({
				errorMessage,
				errorType: 'otpSendFailed',
			});
			throw new Error(errorMessage);
		} catch (error) {
			let errorMessage;

			// Check if this is a network error (no response at all)
			if (!error.response) {
				errorMessage = intl.formatMessage({
					id: 'loginErrors.networkErrorMsg',
				});
			} else if (error.response.data) {
				// Server responded with error data - use our error mapping
				errorMessage = this.getOTPErrorMessage(error.response.data, intl);
			} else {
				// Server responded but no data - generic OTP error
				errorMessage = intl.formatMessage({ id: 'loginErrors.otpSendFailed' });
			}

			this.setState({
				errorMessage,
				errorType: 'otpSendFailed',
			});

			// Don't start timer here - let the calling method handle timer logic
			throw error;
		}
	};

	onSubmit = async ({ username, password, twoFACode }, { setSubmitting }) => {
		const { loginUrl, login2FAUrl, intl, history } = this.props;
		const { accessLevel, show2FA } = this.state;
		try {
			if (!show2FA) {
				// Initial login attempt
				this.setState({
					errorMessage: null,
					errorType: null,
				});
				const { data } = await axios.post(
					loginUrl,
					{
						username,
						password,
						source: 'cp3',
					},
					{ withCredentials: true }
				);
				// Check if user requires 2FA
				if (
					data.user &&
					data.user.accessLevel === 4 &&
					data.user.enableTwoFactorAuth === '1'
				) {
					// Check if mobile number is available for 2FA
					if (!data.user.mobileNumber || data.user.mobileNumber.trim() === '') {
						// Mobile number not available - show error and stay on login page
						this.setState({
							errorMessage: intl.formatMessage({
								id: 'loginErrors.mobileNumberRequired',
							}),
							errorType: 'mobileNumberRequired',
						});
						setSubmitting(false);
						return;
					}

					if ( data.user.accessCodeResponse.status === 'error' && data.user.accessCodeResponse.message === 'invalidPhoneNumberFormat' ) {
						this.setState({
							errorMessage: intl.formatMessage({
								id: 'loginErrors.invalidPhoneNumberFormat',
							}),
							errorType: 'invalidPhoneNumberFormat',
						});
						setSubmitting(false);
						return;
					}

					if (
						data.user.accessCodeResponse.status === 'error' &&
						data.user.accessCodeResponse.message ===
							'accessCodeTwilioBlockedNumber'
					) {
						this.setState({
							errorMessage: intl.formatMessage(
								{ id: 'loginErrors.accessCodeTwilioBlockedNumber' },
								{
									retry_after_hours:
										data.user.accessCodeResponse.retry_after_hours || 24,
								}
							),
							errorType: 'accessCodeTwilioBlockedNumber',
							twoFAResendDisabled: true,
							twoFATimeRemaining: 0,
							twoFATimer: null,
							show2FA: false,
						});
						this.resetToLoginForm();
						setSubmitting(false);
						return;
					}

					if (
						data.user.accessCodeResponse.status === 'error' &&
						(data.user.accessCodeResponse.message === 'accessCodeSendFailed' ||
							data.user.accessCodeResponse.message === 'accessCodeSendCooldown')
					) {
						this.setState({
							errorMessage: intl.formatMessage({
								id: `loginErrors.${data.user.accessCodeResponse.message}`,
							}),
							errorType: data.user.accessCodeResponse.message,
							twoFAResendDisabled: false,
							twoFATimeRemaining: 0,
							twoFATimer: null,
							show2FA: false,
						});
						this.resetToLoginForm();
						setSubmitting(false);
						return;
					}

					if (
						data.user.accessCodeResponse.status === 'error' &&
						data.user.accessCodeResponse.message === 'accessCodeInvalidAttempts'
					) {
						this.setState({
							errorMessage: intl.formatMessage({
								id: 'loginErrors.accessCodeInvalidAttempts',
							}),
							errorType: 'accessCodeInvalidAttempts',
							twoFAResendDisabled: false,
							twoFATimeRemaining: 0,
							twoFATimer: null,
							show2FA: false,
						});
						this.resetToLoginForm();
						setSubmitting(false);
						return;
					}

					if (
						data.user.accessCodeResponse.status === 'error' &&
						(data.user.accessCodeResponse.message ===
							'accessCodeTwilioSendLimit' ||
							data.user.accessCodeResponse.message ===
								'accessCodeTwilioVerifyLimit')
					) {
						// If too many attempts, disable resend button
						this.setState({
							errorMessage: intl.formatMessage(
								{ id: `loginErrors.${data.user.accessCodeResponse.message}` },
								{
									retry_after_minutes:
										data.user.accessCodeResponse.retry_after_minutes || 1,
								}
							),
							errorType: data.user.accessCodeResponse.message,
							twoFAResendDisabled: true,
							twoFATimeRemaining: 0,
							twoFATimer: null,
							show2FA: false,
						});
						this.resetToLoginForm();
						setSubmitting(false);
						return;
					}

					// Mobile number is available - proceed with 2FA
					// Store login data and switch to 2FA mode
					this.setState({
						show2FA: true,
						loginData: data,
						user: data.user,
						errorMessage: null,
						errorType: null,
					});
					this.start2FATimer();
					setSubmitting(false);
					return;
				}else{
					// Continue with normal login flow for non-2FA users
					await this.handleLoginSuccess(data, username);
				}
			} else {
				// 2FA verification step - check if code is expired
				if (this.is2FAExpired()) {
					this.setState({
						errorMessage: intl.formatMessage({ id: 'loginErrors.expired2FA' }),
						errorType: 'expired2FA',
						twoFAResendDisabled: false,
						twoFATimeRemaining: 0,
					});
					// Clean up the expired timer
					if (this.state.twoFATimer) {
						clearTimeout(this.state.twoFATimer);
						this.setState({ twoFATimer: null });
					}
					setSubmitting(false);
					return;
				}

				try {
					const { data: data2FA } = await axios.post(
						login2FAUrl,
						{
							username,
							pin: twoFACode,
							mobileNumber: this.state.loginData.user.mobileNumber,
							userId: this.state.loginData.user.id,
							dataname: this.state.loginData.user.dataname,
							PHPSESSID: this.state.loginData.user.PHPSESSID,
							source: 'cp3',
						},
						{ withCredentials: true }
					);

					if (data2FA.success === true) {
						// Clean up the 2FA timer before proceeding
						this.cleanup2FATimer();

						// Use the original login data but continue the flow
						await this.handleLoginSuccess(this.state.loginData, username);
						// this.setState({ show2FA: false, twoFAError: null });
					} else {
						// Use backend error code if available, otherwise use message
						const errorMessage = this.getOTPErrorMessage(data2FA, intl);

						// Keep the timer running even after verification failure
						// Do not stop or reduce the timer - let it continue for full duration

						this.setState({
							errorMessage,
							errorType: 'otpInvalid',
						});
					}
				} catch (twoFAError) {
					// Handle 2FA verification errors specifically
					let twoFAMessage = '';

					if (!twoFAError.response) {
						twoFAMessage = intl.formatMessage({
							id: 'loginErrors.networkErrorMsg',
						});
					} else if (
						twoFAError.response.headers &&
						twoFAError.response.headers['x-session']
					) {
						history.push(
							`/refresh_page?code=${twoFAError.response.headers['x-session']}`
						);
						return;
					} else {
						const { data } = twoFAError.response;

						if (data.status === 'error' && data.message ==='accessCodeTwilioBlockedNumber') {
							this.setState({
								errorMessage: intl.formatMessage(
									{ id: 'loginErrors.accessCodeTwilioBlockedNumber' },
									{ retry_after_hours: data.retry_after_hours || 24 }
								),
								errorType: 'accessCodeTwilioBlockedNumber',
								twoFAResendDisabled: true,
								twoFATimeRemaining: 0,
								twoFATimer: null,
								show2FA: false,
							});
							this.resetToLoginForm();
							setSubmitting(false);
							return;
						}

						if (data.status === 'error' && (data.message === 'accessCodeSendFailed' || data.message === 'accessCodeSendCooldown')) {
							this.setState({
								errorMessage: intl.formatMessage({
									id: `loginErrors.${data.message}`,
								}),
								errorType: data.message,
								twoFAResendDisabled: false,
								twoFATimeRemaining: 0, 
								twoFATimer: null,
								show2FA: false,
							});
							this.resetToLoginForm();
							setSubmitting(false);
							return;
						}

						if (data.status=== 'error' && data.message === 'accessCodeInvalidAttempts') {
							this.setState({
								errorMessage: intl.formatMessage({
									id: 'loginErrors.accessCodeInvalidAttempts',
								}),
								errorType: 'accessCodeInvalidAttempts',
								twoFAResendDisabled: false,
								twoFATimeRemaining: 0,
								twoFATimer: null,
								show2FA: false,
							});
							this.resetToLoginForm();
							setSubmitting(false);
							return;
						}

						if (
							data.status=== 'error' && (data.message === 'accessCodeTwilioSendLimit' ||
							data.message === 'accessCodeTwilioVerifyLimit')
						) {
							// If too many attempts, disable resend button
							this.setState({
								errorMessage: intl.formatMessage(
									{ id: `loginErrors.${data.message}` },
									{ retry_after_minutes: data.retry_after_minutes || 1 }
								),
								errorType: data.message,
								twoFAResendDisabled: true,
								twoFATimeRemaining: 0,
								twoFATimer: null,
								show2FA: false,
							});
							this.resetToLoginForm();
							setSubmitting(false);
							return;
						}
						if (data) {
							twoFAMessage = this.getOTPErrorMessage(data, intl);
						} else {
							twoFAMessage = intl.formatMessage({
								id: 'loginErrors.otpInvalid',
							});
						}
					}

					this.setState({
						errorMessage: twoFAMessage,
						errorType: 'twoFAError',
					});
					setSubmitting(false);
					return;
				}
			}
		} catch (error) {
			let message = '';
			let localAccessLevel = accessLevel;
			if (isEmpty(error.response)) {
				message = intl.formatMessage({ id: 'loginErrors.networkErrorMsg' });
			} else if (error.response.headers['x-session']) {
				history.push('/refresh_page?code='+error.response.headers['x-session']);
			} else {
				const { data } = error.response;
				localAccessLevel = data.accessLevelRequired
					? data.accessLevelRequired
					: false;
				if (data.code === 'mfa_error') {
					if (data.message ==='accessCodeTwilioBlockedNumber') {
						this.setState({
							errorMessage: intl.formatMessage(
								{ id: 'loginErrors.accessCodeTwilioBlockedNumber' },
								{ retry_after_hours: data.retry_after_hours || 24 }
							),
							errorType: 'accessCodeTwilioBlockedNumber',
							twoFAResendDisabled: true,
							twoFATimeRemaining: 0,
							twoFATimer: null,
							show2FA: false,
						});
						this.resetToLoginForm();
						setSubmitting(false);
						return;
					}

					if (data.message === 'accessCodeTwilioSendLimit' || data.message === 'accessCodeTwilioVerifyLimit') {
						this.setState({
							errorMessage: intl.formatMessage(
								{ id: `loginErrors.${data.message}` },
								{
									retry_after_minutes:
										data.retry_after_minutes || 1,
								}
							),
							errorType: data.message,
							twoFAResendDisabled: true,
							twoFATimeRemaining: 0,
							twoFATimer: null,
							show2FA: false,
						});
						this.resetToLoginForm();
						setSubmitting(false);
						return;
					}
						
					this.setState({
						errorMessage: intl.formatMessage({
							id: `loginErrors.${data.message}`,
						}),
						errorType: data.message,
						twoFAResendDisabled: false,
						twoFATimeRemaining: 0,
						twoFATimer: null,
						show2FA: false,
					});
					this.resetToLoginForm();
					setSubmitting(false);		
					return;
				} else {
					message = intl.formatMessage({ id: `hubqlErrors.${data.code}` });
				}
			}

			// Route errors to correct state based on current mode
			if (show2FA) {
				this.setState({
					errorMessage: message,
					errorType: 'twoFAError',
				});
			} else {
				this.setState({
					errorMessage: message,
					errorType: null, // Reset error type for generic errors
					accessLevel: localAccessLevel,
				});
			}
		} finally {
			setSubmitting(false);
		}
	};

	componentWillMount()
	{
		loadProtect();
	}

	cleanup2FATimer = () => {
		if (this.state.twoFATimer) {
			clearTimeout(this.state.twoFATimer);
			this.setState({
				twoFATimer: null,
				twoFAResendDisabled: false,
				twoFATimeRemaining: 0,
			});
		}
	};

	reset2FAState = () => {
		this.cleanup2FATimer();
		this.setState({
			show2FA: false,
			twoFACode: '',
			errorMessage: null,
			errorType: null,
			twoFAGeneratedAt: null,
			loginData: null,
			formPassword: '',
		});
	};

	resetToLoginForm = () => {
		this.cleanup2FATimer();
		
		// Get username from multiple possible sources BEFORE clearing state
		const retainedUsername = this.state.loginData?.user?.username || 
								 this.state.user?.username || 
								 this.props.username || 
								 this.formikRef?.values?.username ||
								 '';
		
		this.setState({
			show2FA: false,
			twoFACode: '',
			twoFAGeneratedAt: null,
			loginData: null,
			formPassword: '',
		});
		
		// Reset the form values but retain the username
		if (this.formikRef) {
			this.formikRef.resetForm({
				values: {
					username: retainedUsername,
					password: '',
					twoFACode: '',
				},
			});
			
			// Also explicitly set the username field to ensure it updates
			this.formikRef.setFieldValue('username', retainedUsername);
		}
		
		// Update the context username to ensure it's retained
		if (retainedUsername && this.props.updateUsername) {
			this.props.updateUsername(retainedUsername);
		}
	};

	componentWillUnmount() {
		this.cleanup2FATimer();
	}

	getOTPErrorMessage = (data, intl) => {
		// Handle different response structures from backend
		
		// Extract parameters that might be needed for error messages
		const errorParams = {
			retry_after_minutes: data.retry_after_minutes || 1,
			retry_after_hours: data.retry_after_hours || 24,
		};

		// First, try to map error codes to localized messages
		if (data.error && typeof data.error === 'string') {
			// Try to map to OTP-specific error messages first
			const otpErrorKey = `loginErrors.${data.error}`;
			try {
				return intl.formatMessage({ id: otpErrorKey }, errorParams);
			} catch (e) {
				// If OTP mapping fails, try hubql errors
				try {
					return intl.formatMessage({ id: `hubqlErrors.${data.error}` });
				} catch (e2) {
					console.warn(`No localization found for error code: ${data.error}`);
					// Don't return the raw error code, fall through to check for message
				}
			}
		}

		if (data.message && typeof data.message === 'string') {
			// Check if message is a localization key
			const messageKey = `loginErrors.${data.message}`;
			try {
				return intl.formatMessage({ id: messageKey }, errorParams);
			} catch (e) {
				// If not a key, return the message as-is
				return data.message;
			}
		}

		// Handle nested error objects
		if (data.error && typeof data.error === 'object' && data.error.message) {
			const nestedMessageKey = `loginErrors.${data.error.message}`;
			try {
				return intl.formatMessage({ id: nestedMessageKey }, errorParams);
			} catch (e) {
				return data.error.message;
			}
		}

		// Check status for error information
		if (
			data.status &&
			typeof data.status === 'string' &&
			data.status !== 'success'
		) {
			const statusKey = `loginErrors.${data.status}`;
			try {
				return intl.formatMessage({ id: statusKey }, errorParams);
			} catch (e) {
				return data.status;
			}
		}

		// Try to map backend error codes to localization keys
		if (data.code) {
			try {
				return intl.formatMessage({ id: `hubqlErrors.${data.code}` });
			} catch (e) {
				console.warn(`No localization found for error code: ${data.code}`);
				// Don't return the raw error code, fall through to generic message
			}
		}

		// Final fallback to generic error message
		return intl.formatMessage({ id: 'loginErrors.otpSendFailed' });
	};

	render() {
		const {
			errorMessage,
			errorType,
			accessLevel,
			showContactInfoModal,
			businessInfo,
			user,
			show2FA,
			twoFAResendDisabled,
			twoFATimeRemaining,
		} = this.state;
		const { title, isAdmin, intl, width, username: propsUsername } = this.props;

		// Pre-compute error type checks for better performance
		const shouldShowPasswordResetLink =
			!accessLevel &&
			!isAdmin &&
			errorType !== 'mobileNumberRequired' &&
			errorType !== 'invalidPhoneNumberFormat' &&
			errorType !== 'accessCodeInvalidAttempts' &&
			errorType !== 'accessCodeTwilioVerifyLimit' &&
			errorType !== 'accessCodeTwilioSendLimit' &&
			errorType !== 'accessCodeSendCooldown' &&
			errorType !== 'accessCodeTwilioBlockedNumber' &&
			errorType !== 'accessCodeSendFailed' &&
			errorType !== 'expired2FA' &&
			errorType !== 'twoFAError' &&
			errorType !== 'otpSendFailed' &&
			errorType !== 'otpInvalid';
		try {
			if (apolloSubscriptionTransportClient.status === 1) {
				apolloSubscriptionTransportClient.close();
			}
		}
		catch (e) {
			console.error("Error closing Apollo subscription client: ", e);
		}

		return (
			<Formik
				ref={formik => (this.formikRef = formik)}
				initialValues={{ username: propsUsername || '', password: '', twoFACode: '' }}
				enableReinitialize={true}
				validationSchema={validationSchema}
				onSubmit={this.onSubmit}
			>
				{({
					values: { username, password, twoFACode },
					errors,
					handleSubmit,
					handleChange,
					handleBlur,
					touched,
					isValid,
					isSubmitting,
					setFieldValue,
				}) => (
					<StyledForm>
						<div>
							<Typography className="login-form-text login-form-title">
								<FormattedMessage id={`commonStrings.${title}`} />
							</Typography>
						</div>
						{isWidthDown('xs', width) && (
							<div>
								<Typography className="login-form-text login-form-welcome-message">
									<FormattedMessage id="commonStrings.mobileWelcomeMessage" />
								</Typography>
							</div>
						)}
						{!errorMessage && <Spacer height={20} />}
						{errorMessage && (
							<StyledFormMessage>
								<Typography
									component="span"
									className="login-form-text login-form-error"
								>
									{errorMessage}
									{shouldShowPasswordResetLink && (
										<>
											{' '}
											<StyledLink
												Component={Link}
												to="/password_reset"
												onClick={() => this.onChangeForm(username)}
												color={Colors.WHITE}
												textDecoration="underline"
											>
												<FormattedMessage id="commonStrings.recoverPassword" />
											</StyledLink>
										</>
									)}
								</Typography>
							</StyledFormMessage>
						)}
						{!show2FA && (
							<>
								<TextField
									className="login-form-input"
									label={intl.formatMessage({ id: 'commonStrings.username' })}
									type="text"
									name="username"
									value={username}
									autoComplete="username"
									error={touched.username && errors.username}
									onChange={handleChange}
									onBlur={handleBlur}
									initialFocus
								/>
								<Spacer height={20} />
								<TextField
									className="login-form-input"
									label={intl.formatMessage({ id: 'commonStrings.password' })}
									type="password"
									name="password"
									value={password}
									error={touched.password && errors.password}
									autoComplete="new-password"
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								<Spacer height={5} />
							</>
						)}
						{show2FA && (
							<>
								<TextField
									className="login-form-input"
									label={intl.formatMessage({ id: 'commonStrings.twoFACode' })}
									type="text"
									name="twoFACode"
									value={twoFACode}
									error={touched.twoFACode && errors.twoFACode}
									onChange={e => {
										setFieldValue('twoFACode', e.target.value);
										this.setState({ errorMessage: null, errorType: null });
									}}
									onBlur={handleBlur}
									initialFocus
								/>
								<Spacer height={5} />
							</>
						)}
						{!isAdmin && (
							<LinkWrapper>
								{(() => {
									if (!show2FA) {
										return (
											<StyledLink
												className="login-form-password-reset-link"
												Component={Link}
												to="/password_reset"
												onClick={() => this.onChangeForm(username)}
											>
												<Typography className="login-form-text login-form-forgot-password">
													<FormattedMessage id="commonStrings.forgotPassword" />
												</Typography>
											</StyledLink>
										);
									}

									// Add spacing between 2FA error and timer/resend link
									if (show2FA && errorMessage) {
										return (
											<>
												<Spacer height={10} />
												{(() => {
													if (twoFAResendDisabled) {
														return (
															<Typography className="login-form-text login-form-forgot-password">
																{`Verification Code expires in ${twoFATimeRemaining} seconds`}
															</Typography>
														);
													}

													if (twoFATimeRemaining > 0) {
														return (
															<>
																<Typography className="login-form-text login-form-forgot-password">
																	{`Verification Code expires in ${twoFATimeRemaining} seconds`}
																</Typography>
																<Spacer height={5} />
																<StyledLink
																	className="login-form-password-reset-link"
																	Component={Link}
																	onClick={this.handleResend2FA}
																>
																	<Typography className="login-form-text login-form-forgot-password">
																		<FormattedMessage id="commonStrings.resend2FA" />
																	</Typography>
																</StyledLink>
															</>
														);
													}

													return (
														<StyledLink
															className="login-form-password-reset-link"
															Component={Link}
															onClick={this.handleResend2FA}
														>
															<Typography className="login-form-text login-form-forgot-password">
																<FormattedMessage id="commonStrings.resend2FA" />
															</Typography>
														</StyledLink>
													);
												})()}
											</>
										);
									}

									if (twoFAResendDisabled) {
										const message = `Verification Code expires in ${twoFATimeRemaining} seconds`;
										return (
											<Typography className="login-form-text login-form-forgot-password">
												{message}
											</Typography>
										);
									}

									if (twoFATimeRemaining > 0) {
										return (
											<>
												<Typography className="login-form-text login-form-forgot-password">
													{`Verification Code expires in ${twoFATimeRemaining} seconds`}
												</Typography>
												<Spacer height={5} />
												<StyledLink
													className="login-form-password-reset-link"
													Component={Link}
													onClick={this.handleResend2FA}
												>
													<Typography className="login-form-text login-form-forgot-password">
														<FormattedMessage id="commonStrings.resend2FA" />
													</Typography>
												</StyledLink>
											</>
										);
									}

									return (
										<StyledLink
											className="login-form-password-reset-link"
											Component={Link}
											onClick={this.handleResend2FA}
										>
											<Typography className="login-form-text login-form-forgot-password">
												<FormattedMessage id="commonStrings.resend2FA" />
											</Typography>
										</StyledLink>
									);
								})()}
							</LinkWrapper>
						)}
						<Spacer height={20} />
						<Button
							disabled={!isValid || isSubmitting || (show2FA && !twoFACode?.trim())}
							type="submit"
							kind="success"
							fluid
							onClick={handleSubmit}
						>
							<FormattedMessage id="commonStrings.signIn" />
						</Button>
						{/* disableing the pop up
						 <SwitchCP4Modal
							open={showSwitchCP4Modal}
							username={username}
							onClose={() => this.setState({ showSwitchCP4Modal: false })}
							navigateToOldCP={this.navigateToOldCP}
						/> */}
						<ContactInfoModal
							open={showContactInfoModal}
							businessInfo={businessInfo}
							user={user}
							onClose={this.onContactInfoCompleted}
							onCompleted={this.onContactInfoCompleted}
							onCancel={this.onContactInfoCancelled}
							onError={this.onContactInfoError}
						/>
					</StyledForm>
				)}
			</Formik>
		);
	}
}

export default compose(
	injectIntl,
	withWidth(),
	withAuthContext(({ updateUsername, username }) => ({
		updateUsername,
		username,
	})),
	withRouter
)(LoginForm);
